import { ExchangeForm } from '@exodus/exchange-ui';
import type { Asset } from '@passkeys/assets';
import Skeleton from '@passkeys/ui/skeleton';
import Text from '@passkeys/ui/text';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';

import exodus from '@/ui/exodus.js';
import createSortedAssetsSelector from '@/ui/flux/fiat-balances/selectors/create-sorted-assets';
import type { Sorting } from '@/ui/flux/fiat-balances/selectors/create-sorted-assets-selector-factory';
import useModal from '@/ui/hooks/use-modal.js';
import type { ReceiveFailModalAction } from '@/ui/modals/receive-fail-modal/receive-fail-modal.js';
import showReceiveFailModal from '@/ui/modals/receive-fail-modal/receive-fail-modal.js';
import type { ReceiveStartedModalAction } from '@/ui/modals/receive-started-modal/receive-started-modal.js';
import showReceiveStartedModal from '@/ui/modals/receive-started-modal/receive-started-modal.js';
import type { SwapSuccessModalAction } from '@/ui/modals/swap-success-modal/swap-success-modal.js';
import showSwapSuccessModal, {
  SWAP_SUCCESS_MODAL_TITLE,
} from '@/ui/modals/swap-success-modal/swap-success-modal.js';
import showTokenPickerModal from '@/ui/modals/token-picker-modal/token-picker-modal';
import { cn } from '@/ui/utils/classnames';
import useExchangeResult from './hooks/use-exchange-result.js';
import Divider, { DividerSkeleton } from './parts/divider.js';
import FormError from './parts/error.js';
import FormLayout from './parts/form-layout.js';
import FromFields, { FromFieldsSkeleton } from './parts/from-fields.js';
import PercentageSwitch from './parts/percent-switch.js';
import SubmitButton from './parts/submit-button.js';
import ToFields, { ToFieldsSkeleton } from './parts/to-fields.js';
import type { SwapFormVariant } from './types.js';

// TODO: Move sorting to token picker? Needs to unify with fiat.
const ORDER = [
  ['fiatBalance', 'desc'],
  ['isBaseAsset', 'desc'],
  ['displayName', 'asc'],
] as Sorting;

type ModalAction = ReceiveStartedModalAction | SwapSuccessModalAction;

type SwapFormProps = {
  variant?: SwapFormVariant;
  fromAssetName?: string;
  toAssetName?: string;
  disabledToAsset?: boolean;
  disabledFlip?: boolean;
  noPercentageSwitch?: boolean;
  referrer?: string;
  submitButtonClassName?: string;
  primaryModalAction: ModalAction;
  alternativeModalAction: ModalAction;
  failModalAction: ReceiveFailModalAction;
};

function SwapForm({
  variant = 'vertical',
  fromAssetName: defaultFromAssetName,
  toAssetName: defaultToAssetName,
  disabledToAsset,
  disabledFlip,
  noPercentageSwitch,
  referrer,
  submitButtonClassName,
  primaryModalAction,
  alternativeModalAction,
  failModalAction,
}: SwapFormProps) {
  const exchangeResult = useExchangeResult();
  const modal = useModal();

  const sortedAssets = useSelector(createSortedAssetsSelector(ORDER));

  const handleAssetClick = async ({ assets }: { assets: Asset[] }) => {
    const sorted = sortedAssets.filter((asset) => assets.includes(asset));
    const { result } = await showTokenPickerModal({ assets: sorted });
    return result;
  };

  useEffect(() => {
    const {
      status,
      toAmount,
      toAssetName,
      fromAmount,
      fromAssetName,
      userRejected,
    } = exchangeResult;
    if (!status || !toAmount || !toAssetName) {
      return;
    }

    const failed = status === 'failed';
    const inProgress = status === 'inProgress';
    const complete = status === 'complete';

    if (!modal && failed) {
      // Avoid displaying the fail modal if a user intentionally rejected the transaction.
      if (!userRejected) {
        showReceiveFailModal({
          assetName: toAssetName,
          type: 'swap',
          primaryAction: failModalAction,
        });
      }

      exodus.exchange.ackExchange();
    }

    if (!modal && inProgress) {
      showReceiveStartedModal({
        amount: toAmount.toDefaultNumber(),
        assetName: toAssetName,
        type: 'swap',
        primaryAction: primaryModalAction,
        alternativeAction: alternativeModalAction,
        onClose: exodus.exchange.ackExchange,
      });
    }

    if (complete) {
      if (modal?.title === SWAP_SUCCESS_MODAL_TITLE) {
        return;
      }

      if (modal) {
        modal.close();
      }

      showSwapSuccessModal({
        toAmount,
        toAssetName,
        fromAmount,
        fromAssetName,
        primaryAction: primaryModalAction,
        alternativeAction: alternativeModalAction,
        onClose: exodus.exchange.ackExchange,
      });
    }
  }, [
    exchangeResult,
    failModalAction,
    referrer,
    primaryModalAction,
    alternativeModalAction,
    defaultFromAssetName,
    defaultToAssetName,
    modal,
  ]);

  return (
    <ExchangeForm
      config={{
        defaultFromAsset: 'ethereum',
        defaultToAsset: 'bitcoin',
      }}
      params={{
        origin: referrer,
        fromAssetName: defaultFromAssetName,
        toAssetName: defaultToAssetName,
      }}
    >
      <FormLayout variant={variant}>
        <FromFields variant={variant} onAssetClick={handleAssetClick} />

        <Divider variant={variant} disabled={disabledFlip} />

        <ToFields
          variant={variant}
          disabled={disabledToAsset}
          onAssetClick={handleAssetClick}
        />

        {!noPercentageSwitch && variant !== 'horizontal' && (
          <ExchangeForm.PercentageSwitch
            Component={PercentageSwitch}
            className="col-span-2 mx-6 mt-4"
          />
        )}
      </FormLayout>

      <FormError />

      <footer className="flex flex-col gap-y-4">
        <ExchangeForm.SubmitButton
          Component={SubmitButton}
          className={submitButtonClassName}
        />
        <Text
          size={12}
          className={cn(
            'text-center font-normal text-neutral-200 theme-dark:text-neutral-400',
          )}
        >
          Swap services provided by third-party APIs.
        </Text>
      </footer>
    </ExchangeForm>
  );
}

export function SwapFormSkeleton({
  variant = 'vertical',
  noPercentageSwitch,
}: Pick<SwapFormProps, 'variant' | 'noPercentageSwitch'>) {
  return (
    <FormLayout variant={variant}>
      <FromFieldsSkeleton variant={variant} />

      <DividerSkeleton variant={variant} />

      <ToFieldsSkeleton variant={variant} />

      {!noPercentageSwitch && variant !== 'horizontal' && (
        <div className="col-span-2 flex">
          <Skeleton height={40} borderRadius={9999} className="mx-6 mt-4" />
        </div>
      )}
    </FormLayout>
  );
}

export default SwapForm;
